import { useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { mappedLocales } from '@core/Utils/Intl/MappedLocales';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import Button from '@gds/Button/Button';
import { Input } from '@gds/Input/Input';
import { SegmentedButtons } from '@gds/SegmentedButtons/SegmentedButtons';
import Slider from '@gds/Slider/Slider';
import { Typography } from '@gds/Typography/Typography';

import { useRangeFilters } from 'Hooks/SearchFilters/UseRangeFilters';

import type { FilterControlBaseProps, PlpFiltersMessagesProps } from '../../FilterPanel.entity';
import { CONDITION_TYPES, TEST_RANGE } from './YearFilter.constants';

import styles from './YearFilter.module.css';

type YearFilterProps = FilterControlBaseProps;

export const YearFilter = ({ locale, strings }: YearFilterProps) => {
  const filtersList = useAtomValue(filtersListAtom);
  const minAvailable = TEST_RANGE[0].toString();
  const maxAvailable = TEST_RANGE[TEST_RANGE.length - 1].toString();
  const { totalCount } = useAtomValue(aggregationsAtom);

  const btnDataIntl = CONDITION_TYPES.filter(type => type.locales.includes(locale)).map(type => ({
    ...type,
    label: strings[type.id as keyof PlpFiltersMessagesProps],
  }));

  const { handleChange, gteInputProps, lteInputProps, sliderProps, canReset, reset } =
    useRangeFilters('year', TEST_RANGE, [minAvailable, maxAvailable]);

  const condition = getFilterFromArray('stock-condition', filtersList)?.value as string | undefined;

  return (
    <section className={styles.yearFilter}>
      <Typography className={styles.infoLine} variant="subheading4">
        {strings.yearInfoLine}
      </Typography>
      <SegmentedButtons
        buttons={btnDataIntl}
        className={styles.conditionTypeBtns}
        currentValue={condition}
        onChange={val => handleChange([['stock-condition', val as string]])}
      />
      <div className={styles.minMaxInputs}>
        <Input {...gteInputProps} label={strings.min} type="number" min={minAvailable} />
        <Input {...lteInputProps} label={strings.max} type="number" max={maxAvailable} />
      </div>
      <div className={styles.auxInfo}>
        <Typography Component="span" className={styles.resultCount} variant="caption2">
          ({totalCount.toLocaleString(mappedLocales[locale])})
        </Typography>
        <Button
          color="secondary"
          disabled={!canReset(['stock-condition'])}
          onClick={() => reset(['stock-condition'])}
          size="small"
          variant="link"
        >
          {strings.reset}
        </Button>
      </div>
      <Slider {...sliderProps} className={styles.priceRangeSlider} dataTestId="year-slider" />
    </section>
  );
};
